import { useState, useCallback } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { useParams } from 'react-router-dom';
import {
    sendMessageToAPI,
    executeToolCalls,
    sendFunctionResultsToAPI,
    isGPT5Enabled,
    getGPT5ParameterOptions
} from '../services/apiClient';
import { convertMessagesToApiFormat } from '../services/chatApiFormatter';
import { logger } from '../utils/logger';

/**
 * Custom hook to manage the chat state, message sending, tool call handling,
 * and related API interactions.
 *
 * @param {Array} initialDataSources - Data sources needed for tool execution.
 * @returns {object} - An object containing chat state and handler functions:
 *   - messages: Array of chat message objects.
 *   - setMessages: Function to update messages state.
 *   - inputMessage: Current value of the input field.
 *   - setInputMessage: Function to update input message state.
 *   - isProcessing: Boolean indicating if a message is currently being processed.
 *   - apiCalls: Array of tool calls made (for display).
 *   - expandedApiCalls: Object tracking expanded state of tool calls in UI.
 *   - error: Error message string related to chat operations, otherwise null.
 *   - handleSendMessage: Function to send the current input message.
 *   - resetConversation: Function to reset the chat state.
 *   - toggleApiCall: Function to toggle the expanded state of a tool call.
 *   - reasoningEffort: Current GPT-5 reasoning effort setting.
 *   - verbosity: Current GPT-5 verbosity setting.
 *   - setReasoningEffort: Function to update reasoning effort.
 *   - setVerbosity: Function to update verbosity level.
 *   - resetGPT5Parameters: Function to reset GPT-5 parameters to defaults.
 *   - getGPT5Parameters: Function to get current GPT-5 parameter values.
 *   - isGPT5Enabled: Boolean indicating if GPT-5 features are enabled.
 */
export const useChatManager = (initialDataSources = [], collectionHeaders = {}) => {
    const [messages, setMessages] = useState([{
        text: "Hi! I am your TenXer assistant, how can I help you today?",
        author: 'ai',
        timestamp: new Date().toLocaleTimeString()
    }]);
    const [inputMessage, setInputMessage] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [apiCalls, setApiCalls] = useState([]); // For tool call display
    const [expandedApiCalls, setExpandedApiCalls] = useState({}); // For tool call display UI state
    const [error, setError] = useState(null); // Chat-specific errors
    
    // GPT-5 parameter state
    const gpt5Options = getGPT5ParameterOptions();
    const [reasoningEffort, setReasoningEffortState] = useState(gpt5Options.defaults.reasoningEffort);
    const [verbosity, setVerbosityState] = useState(gpt5Options.defaults.verbosity);

    const { user, getAccessTokenSilently } = useAuth0();
    const { collectionId } = useParams();
    // Use initialDataSources passed as argument, assuming it's kept up-to-date by the parent component
    const dataSources = initialDataSources; 

    const toggleApiCall = useCallback((callIndex) => {
        setExpandedApiCalls(prev => ({
            ...prev,
            [callIndex]: !prev[callIndex]
        }));
    }, []);
    
    // GPT-5 parameter management functions
    const setReasoningEffort = useCallback((effort) => {
        const validOptions = gpt5Options.reasoningEffort;
        if (!validOptions.includes(effort)) {
            logger.error(`Invalid reasoning effort: ${effort}. Valid options are: ${validOptions.join(', ')}`);
            setError(`Invalid reasoning effort. Valid options are: ${validOptions.join(', ')}`);
            return;
        }
        logger.debug(`Setting reasoning effort to: ${effort}`);
        setReasoningEffortState(effort);
    }, [gpt5Options.reasoningEffort]);
    
    const setVerbosity = useCallback((level) => {
        const validOptions = gpt5Options.verbosity;
        if (!validOptions.includes(level)) {
            logger.error(`Invalid verbosity level: ${level}. Valid options are: ${validOptions.join(', ')}`);
            setError(`Invalid verbosity level. Valid options are: ${validOptions.join(', ')}`);
            return;
        }
        logger.debug(`Setting verbosity to: ${level}`);
        setVerbosityState(level);
    }, [gpt5Options.verbosity]);
    
    const resetGPT5Parameters = useCallback(() => {
        logger.debug('Resetting GPT-5 parameters to defaults');
        setReasoningEffortState(gpt5Options.defaults.reasoningEffort);
        setVerbosityState(gpt5Options.defaults.verbosity);
    }, [gpt5Options.defaults]);
    
    const getGPT5Parameters = useCallback(() => {
        return {
            reasoningEffort,
            verbosity,
            isEnabled: isGPT5Enabled()
        };
    }, [reasoningEffort, verbosity]);

    // Reset conversation state
    const resetConversation = useCallback(() => {
        logger.debug("Resetting conversation...");
        // Keep only the first welcome message
        const newMessages = [{
            text: "Hi! I am your TenXer assistant, how can I help you today?",
            author: 'ai',
            timestamp: new Date().toLocaleTimeString()
        }];

        // Add a system message explaining what happened
        newMessages.push({
            text: "*The conversation has been reset due to an error or user request.*",
            author: 'system',
            timestamp: new Date().toLocaleTimeString(),
            isSystemMsg: true
        });

        setMessages(newMessages);
        setApiCalls([]);
        setExpandedApiCalls({});
        setError(null);
        setIsProcessing(false); // Ensure processing state is reset
        setInputMessage(''); // Clear input
    }, []); // No dependencies needed for reset logic itself


    // Handle tool calls triggered by an AI message
    const handleToolCalls = useCallback(async (toolCalls, messageHistory) => {
        if (!user || !collectionId) {
            setError("Cannot handle tool calls: Missing user or collection context.");
            return;
        }
        
        const executingMsgId = `executing-${Date.now()}`;
        try {
            // Show "thinking" message for tool execution
            setMessages(prev => [...prev, {
                id: executingMsgId,
                text: `*Executing ${toolCalls.length} API call${toolCalls.length > 1 ? 's' : ''}...*`,
                author: 'system',
                timestamp: new Date().toLocaleTimeString(),
                isThinking: true,
                isSystemMsg: true
            }]);

            // Execute all tool calls (client-side)
            // Ensure dataSources is available and up-to-date here
            if (!dataSources || dataSources.length === 0) {
                 logger.warn("Executing tool calls without data sources defined.");
                 // Potentially throw error or handle based on requirements
            }
            const functionResults = await executeToolCalls(toolCalls, dataSources, collectionHeaders);

            // Update UI state for sidebar display
            const callsWithResponses = toolCalls.map((call, index) => ({
                ...call,
                response: functionResults[index]?.content ?? null, // Use nullish coalescing
                timestamp: new Date().toLocaleTimeString()
            }));
            
            // Use functional update for apiCalls to ensure we have the latest state
            let startIndex = 0;
            setApiCalls(prev => {
                startIndex = prev.length; // Get length before update
                return [...prev, ...callsWithResponses];
            });

            // Update expandedApiCalls for new items
            setExpandedApiCalls(prevExpanded => {
                const newExpandedState = { ...prevExpanded };
                for (let i = 0; i < toolCalls.length; i++) {
                    newExpandedState[startIndex + i] = false; // Collapse new calls
                }
                return newExpandedState;
            });


            // Get token and tenantId for the next API call
            const token = await getAccessTokenSilently();
            if (!token) throw new Error("Failed to get access token for sending function results");
            const tenantId = user['https://tenxer.ai/tenant_id'];

            // Find the index of the assistant message that triggered these tool calls
            let lastAssistantMessageWithToolsIndex = -1;
            for (let i = messageHistory.length - 1; i >= 0; i--) {
                if (messageHistory[i].role === 'assistant' && messageHistory[i].tool_calls?.length > 0) {
                    lastAssistantMessageWithToolsIndex = i;
                    break;
                }
            }
            if (lastAssistantMessageWithToolsIndex === -1) {
                throw new Error("Consistency error: No assistant message with tool calls found in provided history for handleToolCalls");
            }

            // Add tool responses to the internal message history (hidden from UI)
            // These are crucial for the subsequent API call
            const toolResponseMessages = functionResults.map(result => ({
                text: typeof result.content === 'string' ? result.content : JSON.stringify(result.content, null, 2),
                author: 'tool',
                timestamp: new Date().toLocaleTimeString(),
                tool_call_id: result.tool_call_id,
                name: result.name, // Ensure name is included if available
                content: typeof result.content === 'string' ? result.content : JSON.stringify(result.content, null, 2), // Add content for formatter
                responding_to_assistant_idx: lastAssistantMessageWithToolsIndex, // Track relation
                isHidden: true // Hide from UI
            }));

            // Add hidden tool responses to the main messages state
            setMessages(prev => [...prev, ...toolResponseMessages]);

            // Prepare history for the API: includes original history up to the assistant message + tool responses
            const historyForApi = messageHistory.slice(0, lastAssistantMessageWithToolsIndex + 1);
            
            // Send function results back to the backend
            const response = await sendFunctionResultsToAPI(
                historyForApi, // Send history up to the assistant message that asked for tools
                functionResults, // Send the actual results
                tenantId,
                collectionId,
                token,
                reasoningEffort, // GPT-5 parameter
                verbosity // GPT-5 parameter
            );

            // Update the executing message to indicate completion
             setMessages(prev => prev.map(msg =>
                 msg.id === executingMsgId
                     ? { ...msg, text: `*API calls completed*`, isCompleted: true, isThinking: false } // Mark as not thinking
                     : msg
             ));

            // Process the AI's response after function execution
            // The 'response' object from sendFunctionResultsToAPI contains the *complete* history
            // including the original request, tool results, and final assistant message.
            if (response?.messages && response?.choices?.length > 0) {
                
                // Use the full message history returned by the backend
                const backendMessages = response.messages;

                // Convert backend format (role/content) back to frontend format (author/text)
                // and filter out the temporary "executing" message
                const newFrontendMessages = backendMessages
                    .filter(msg => msg.role !== 'system') // Filter out system messages from backend
                    .map(msg => ({
                        // Map backend roles/content to frontend author/text
                        // Ensure tool calls are preserved if present
                        text: msg.content || "", // Handle potential null content
                        author: msg.role === 'assistant' ? 'ai' : msg.role, // Map 'assistant' to 'ai'
                        timestamp: new Date().toLocaleTimeString(), // Add timestamp (or derive if backend provided one)
                        tool_calls: msg.tool_calls || null, // Preserve tool calls
                        // Add other potential fields if needed (id, isHidden, etc.) - be careful not to overwrite essential ones
                        // We need to ensure the 'tool' role messages from backend are preserved correctly
                        // for the *next* convertMessagesToApiFormat call.
                        // Let's add necessary fields for tool messages:
                        ...(msg.role === 'tool' && {
                            tool_call_id: msg.tool_call_id,
                            name: msg.name,
                            content: msg.content, // Keep original content for formatter
                            isHidden: true // Keep tool messages hidden in UI by default
                        })
                    }))
                    .filter(msg => !(msg.id === executingMsgId && msg.isCompleted)); // Filter out the completed system message

                setMessages(newFrontendMessages); // Replace state with the full, correct history

                // Process the final assistant message for potential nested tool calls
                const assistantMessageContent = response.choices[0].message;


                // Handle nested tool calls if present
                if (assistantMessageContent.tool_calls?.length > 0) {
                    // Construct the history for the *next* handleToolCalls invocation
                    // History should be:
                    // 1. History sent to sendFunctionResultsToAPI (historyForApi)
                    // 2. The tool results we just got (functionResults formatted for API)
                    // 3. The new assistant message containing the new tool_calls
                    
                    const nextHistory = [...historyForApi]; // Start with history up to previous assistant

                    // Add the tool responses (formatted for API)
                    functionResults.forEach(result => {
                        nextHistory.push({
                            role: 'tool',
                            tool_call_id: result.tool_call_id,
                            name: result.name,
                            content: typeof result.content === 'string' ? result.content : JSON.stringify(result.content)
                        });
                    });

                    // Add the new assistant message itself
                    nextHistory.push({
                        role: 'assistant',
                        content: assistantMessageContent.content || "",
                        tool_calls: assistantMessageContent.tool_calls
                    });

                    // Recursively call handleToolCalls with the new calls and updated history
                    await handleToolCalls(assistantMessageContent.tool_calls, nextHistory);
                }
            } else {
                 // Remove the completed system message if response is invalid
                 setMessages(prev => prev.filter(msg => !(msg.id === executingMsgId && msg.isCompleted)));
                throw new Error("Invalid response structure received after sending function results");
            }

        } catch (err) {
            logger.error('Error handling tool calls:', err);
            const errorMsg = err.message || "Error executing API calls";
            setError(errorMsg);
            // Replace any thinking message with an error message
            setMessages(prev => {
                const withoutThinking = prev.filter(msg => msg.id !== executingMsgId);
                return [...withoutThinking, {
                    text: `Error executing API calls: ${errorMsg}`,
                    author: 'system',
                    timestamp: new Date().toLocaleTimeString(),
                    isError: true,
                    isSystemMsg: true
                }];
            });
        }
    }, [user, collectionId, getAccessTokenSilently, dataSources, setMessages, setApiCalls, setExpandedApiCalls, setError, reasoningEffort, verbosity]); // Added GPT-5 dependencies


    // Handle sending a new user message
    const handleSendMessage = useCallback(async () => {
        if (!inputMessage.trim() || isProcessing) return;

        setIsProcessing(true);
        setError(null);

        const userMessage = {
            text: inputMessage,
            author: 'user',
            timestamp: new Date().toLocaleTimeString()
        };
        const currentInput = inputMessage; // Capture input before clearing
        
        // Determine the next state *before* updating
        // Use the current 'messages' state from the hook's scope plus the new userMessage
        const nextMessagesState = [...messages, userMessage];

        // Update UI state and clear input
        setMessages(nextMessagesState);
        setInputMessage('');

        try {
            if (!user || !collectionId) throw new Error("User or collection context is missing.");
            
            const token = await getAccessTokenSilently();
            if (!token) throw new Error("Failed to get access token");
            const tenantId = user['https://tenxer.ai/tenant_id'];

            // Convert the *next* message state (which includes the user message) to API format
            let apiMessages = convertMessagesToApiFormat(nextMessagesState, null); // Use the explicitly constructed next state
            
            // Add custom system message if this is the first message in the conversation
            if (apiMessages.length === 1 && apiMessages[0].role === 'user') {
                // Add your custom system instructions here
                const systemMessage = {
                    role: 'system',
                    content: 'You are a helpful assistant. Please provide clear, concise answers.'
                    // Customize this message as needed
                };
                apiMessages = [systemMessage, ...apiMessages];
            }
            
            logger.debug("Formatted messages for API:", apiMessages);


            // Call backend API
            const aiResponse = await sendMessageToAPI(
                apiMessages, // Send the formatted history
                tenantId,
                collectionId,
                token,
                reasoningEffort, // GPT-5 parameter
                verbosity // GPT-5 parameter
            );

            // Process AI response
            if (aiResponse?.choices?.length > 0) {
                const assistantMessageContent = aiResponse.choices[0].message;
                const aiMessage = {
                    text: assistantMessageContent.content || "", // Ensure text is always string
                    author: 'ai',
                    timestamp: new Date().toLocaleTimeString(),
                    tool_calls: assistantMessageContent.tool_calls || null
                };

                // Add AI message only if it has content or tool calls
                 if (aiMessage.text.trim() || (aiMessage.tool_calls && aiMessage.tool_calls.length > 0)) {
                    setMessages(prevMessages => [...prevMessages, aiMessage]);
                 }


                // Handle tool calls if present in the response
                if (assistantMessageContent.tool_calls?.length > 0) {
                    // Prepare the history for handleToolCalls:
                    // It should be the messages sent to the API + the assistant response we just received
                    const historyForToolCall = [...apiMessages, {
                        role: 'assistant',
                        content: assistantMessageContent.content || "",
                        tool_calls: assistantMessageContent.tool_calls
                    }];
                    await handleToolCalls(assistantMessageContent.tool_calls, historyForToolCall);
                }
            } else {
                throw new Error("Invalid response structure received from API");
            }

        } catch (err) {
            logger.error('Error sending message:', err);
            const errorMsg = err.message || "I encountered an error processing your request.";
            setError(errorMsg);

            // Check if it's a tool sequence error (common issue)
            const isToolSequenceError = errorMsg.includes("tool_calls") || 
                                        errorMsg.includes("tool' must be a response") ||
                                        errorMsg.includes("message with role 'tool'");

            const errorMessageText = "```diff\n- Error: " + errorMsg +
                (isToolSequenceError ? "\n\nThis might be an issue with the tool call sequence. Resetting the conversation may help." : "") +
                "\n```";
                
            const errorMessage = {
                text: errorMessageText,
                author: 'ai', // Display as AI message for consistency
                timestamp: new Date().toLocaleTimeString(),
                isError: true // Mark as error for potential styling
            };
            setMessages(prevMessages => [...prevMessages, errorMessage]);

            // Optionally add a system message with a reset button for tool errors
            if (isToolSequenceError) {
                setMessages(prevMessages => [
                    ...prevMessages,
                    {
                        text: "Would you like to reset the conversation to fix this issue?",
                        author: 'system',
                        timestamp: new Date().toLocaleTimeString(),
                        isSystemMsg: true,
                        actions: [
                            { label: "Reset Conversation", action: resetConversation }
                        ]
                    }
                ]);
            }
        } finally {
            setIsProcessing(false);
            // Scrolling and focus are handled by useScrollAndFocus hook in the component
        }
    }, [inputMessage, isProcessing, user, collectionId, getAccessTokenSilently, handleToolCalls, resetConversation, setMessages, setInputMessage, setIsProcessing, setError, reasoningEffort, verbosity]); // Added GPT-5 dependencies


    return {
        messages,
        setMessages, // Expose setMessages if external modification is needed (e.g., initial load)
        inputMessage,
        setInputMessage,
        isProcessing,
        apiCalls,
        expandedApiCalls,
        error,
        handleSendMessage,
        resetConversation,
        toggleApiCall,
        
        // GPT-5 parameters and functions
        reasoningEffort,
        verbosity,
        setReasoningEffort,
        setVerbosity,
        resetGPT5Parameters,
        getGPT5Parameters,
        isGPT5Enabled: isGPT5Enabled(),
    };
};